/**
 * DE-318 — which of two OS artifacts is newer, and whether a release's OS part
 * has been left behind by what the device actually runs.
 *
 * An OS artifact name is `<prefix>-v<semver>`: `artomeui-v2.6.1`,
 * `artomeui-v2.7.0-dev.690`, `artomeui-v2.7.0-dev.de298-multi-hardware.807`.
 * Ordering is SemVer 2.0 on the part after `-v` — the same rule the device's
 * `update_common.version_cmp` applies (DE-219): a pre-release sorts below its
 * release, identifiers compare numerically when both are numeric, else as text,
 * and a longer identifier list wins a tie. Never feed it a public release ID
 * (`2026.08.1`): that is CalVer, and lives in releaseId.ts.
 */

interface Parsed {
  core: [number, number, number];
  pre: string[];
}

export function parseOsVersion(name: string | null | undefined): Parsed | null {
  if (!name) return null;
  const m = /-v(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z.-]+))?$/.exec(name);
  if (!m) return null;
  return {
    core: [Number(m[1]), Number(m[2]), Number(m[3])],
    pre: m[4] ? m[4].split('.') : [],
  };
}

/** <0 when a is older, 0 when equal, >0 when newer; null when either cannot be read. */
export function compareOsVersions(a: string | null | undefined, b: string | null | undefined): number | null {
  const pa = parseOsVersion(a);
  const pb = parseOsVersion(b);
  if (!pa || !pb) return null;
  const [a1, a2, a3] = pa.core;
  const [b1, b2, b3] = pb.core;
  if (a1 !== b1) return a1 - b1;
  if (a2 !== b2) return a2 - b2;
  if (a3 !== b3) return a3 - b3;
  if (pa.pre.length === 0 || pb.pre.length === 0) {
    // A release outranks any of its own pre-releases.
    return (pa.pre.length === 0 ? 1 : 0) - (pb.pre.length === 0 ? 1 : 0);
  }
  for (let i = 0; i < Math.min(pa.pre.length, pb.pre.length); i++) {
    const x = pa.pre[i] ?? '';
    const y = pb.pre[i] ?? '';
    if (x === y) continue;
    const nx = /^\d+$/.test(x);
    const ny = /^\d+$/.test(y);
    if (nx && ny) return Number(x) - Number(y);
    if (nx !== ny) return nx ? -1 : 1; // numeric identifiers sort below text ones
    return x < y ? -1 : 1;
  }
  return pa.pre.length - pb.pre.length;
}

/**
 * True when the device runs an OS NEWER than the one the release names. Such a
 * release can never be reported as applied: its OS part cannot match again, and
 * a coupled content part is tagged to that OS. It is not waiting for anything —
 * it has been superseded. An unreadable name is never "superseded": when in
 * doubt, keep today's behaviour.
 */
export function osSuperseded(named: string | null | undefined, running: string | null | undefined): boolean {
  if (!named || !running || named === running) return false;
  const c = compareOsVersions(running, named);
  return c !== null && c > 0;
}

/** The part set a published release names — the shape GET /releases/published returns. */
export interface ReleaseOsParts {
  os?: { version: string } | null;
  osByDeviceType?: Record<string, { version: string }> | null;
}

/**
 * The OS a release names for THIS board: its per-type entry (DE-311) when the
 * release has one for the device's type, else the legacy single `os`.
 */
export function releaseOsFor(parts: ReleaseOsParts | null | undefined,
                             deviceType: string | null | undefined): string | null {
  if (!parts) return null;
  const perType = deviceType ? parts.osByDeviceType?.[deviceType]?.version : undefined;
  return perType ?? parts.os?.version ?? null;
}

/** The fields of the shadow's `release` category this reads. */
export interface ShadowReleaseLike {
  id?: unknown;
  source?: unknown;
  pending_id?: unknown;
}

/**
 * DE-318 — the release a device reports as pending but has moved PAST, or null.
 *
 * A pending release reaches the Portal as `pending_id` alone — what telemetry
 * publishes, and what the row copy of the shadow holds (dev and …031 on
 * 2026-09-24). The live AWS shadow can also carry an `id` beside
 * `source: 'pending'`: AWS merges nested keys, so an `id` telemetry stopped
 * sending survives there. Both shapes are handled. Either way, when
 * the device runs an OS newer than the one the release names for its board, the
 * release can never be reported as applied — it is superseded, not staged.
 */
export function supersededRelease(
  release: ShadowReleaseLike | null | undefined,
  published: ReadonlyArray<{ release_id: string; parts: ReleaseOsParts }> | null | undefined,
  deviceType: string | null | undefined,
  runningOs: string | null | undefined,
): { releaseId: string; namedOs: string; runningOs: string } | null {
  if (!release || release.source !== 'pending') return null;
  const ref = typeof release.pending_id === 'string' ? release.pending_id
    : typeof release.id === 'string' ? release.id : null;
  if (!ref) return null;
  const namedOs = releaseOsFor(published?.find((r) => r.release_id === ref)?.parts, deviceType);
  if (!namedOs || !runningOs || !osSuperseded(namedOs, runningOs)) return null;
  return { releaseId: ref, namedOs, runningOs };
}

/** The device-list fields the dashboard's release column reads (GET /devices). */
export interface ReleaseRowLike {
  release_id: string | null;
  release_source: string | null;
  release_pending_id?: string | null;
  device_type: string | null;
  mender_artifact: string | null;
}

/** DE-318 — what the dashboard's release column shows for one device. */
export type ReleaseCell =
  | { kind: 'released'; id: string; source: string | null }
  | { kind: 'superseded'; releaseId: string; namedOs: string; runningOs: string }
  | { kind: 'staged'; releaseId: string | null }
  | { kind: 'none' };

/**
 * The device page gets the whole `release` object; the dashboard's list row gets
 * only the columns GET /devices selects. A pending release is `pending_id` alone
 * (dev and …031 on 2026-09-24), so the list carries `release_pending_id` too.
 * Both screens go through `supersededRelease`, so they cannot disagree about the
 * same device. A pending row is decided first: an `id` beside `source: 'pending'`
 * names what is waiting, not what is applied. Without the published list, or
 * from a backend that does not send `release_pending_id`, a pending row stays
 * "staged" — never superseded without evidence.
 */
export function releaseCell(
  row: ReleaseRowLike,
  published: ReadonlyArray<{ release_id: string; parts: ReleaseOsParts }> | null | undefined,
): ReleaseCell {
  if (row.release_source === 'pending') {
    const superseded = supersededRelease(
      { id: row.release_id, source: row.release_source, pending_id: row.release_pending_id },
      published, row.device_type, row.mender_artifact);
    if (superseded) return { kind: 'superseded', ...superseded };
    return { kind: 'staged', releaseId: row.release_pending_id ?? row.release_id ?? null };
  }
  if (row.release_id) return { kind: 'released', id: row.release_id, source: row.release_source };
  return { kind: 'none' };
}
